import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MessageCircle, Award, GraduationCap, Users, Heart, CheckCircle, Smile } from "lucide-react";
import { DOCTOR_NAME, getWhatsAppUrl } from "@/lib/constants";
import Breadcrumbs from "@/components/Breadcrumbs";
import CTABanner from "@/components/CTABanner";
import doctorImg from "@/assets/doctor/doctor-hero-clinic.jpg";
import teamGroup1 from "@/assets/team/team-group-1.jpg";
import teamGroup2 from "@/assets/team/team-group-2.jpg";
import clinic1 from "@/assets/clinic/clinic-1.jpg";
import clinic2 from "@/assets/clinic/clinic-2.jpg";
import clinic3 from "@/assets/clinic/clinic-3.jpg";
import clinic4 from "@/assets/clinic/clinic-4.jpg";

const qualifications = [
  { degree: "BDS", institution: "Bachelor of Dental Surgery" },
  { degree: "MDS (Prosthodontics)", institution: "Master of Dental Surgery — Prosthodontics & Crown/Bridge" },
  { degree: "Fellowship in Implantology", institution: "Advanced training in surgical & prosthetic implantology" },
];

const stats = [
  { icon: Users, value: "500+", label: "Happy Patients" },
  { icon: Award, value: "10+", label: "Years Experience" },
  { icon: Heart, value: "11", label: "Dental Services" },
  { icon: Smile, value: "1000+", label: "Smiles Created" },
];

const About = () => (
  <div>
    <section className="bg-secondary py-12 md:py-16">
      <div className="container">
        <Breadcrumbs items={[{ label: "About Dr. Khushboo Mutha" }]} />
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-secondary-foreground mb-4">
              About {DOCTOR_NAME}
            </h1>
            <p className="text-secondary-foreground/80 mb-6">
              Passionate about creating beautiful, healthy smiles — {DOCTOR_NAME} brings over 10 years of expertise
              in advanced dental care to Kalyan.
            </p>
            <Button asChild>
              <a href={getWhatsAppUrl()} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="h-5 w-5 mr-2" /> Chat with Dr. Mutha
              </a>
            </Button>
          </motion.div>
          <motion.div
            className="aspect-[4/3] md:aspect-[5/4] rounded-xl overflow-hidden shadow-lg bg-muted"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <img src={doctorImg} alt={DOCTOR_NAME} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700" width={800} height={600} loading="eager" decoding="async" />
          </motion.div>
        </div>
      </div>
    </section>

    <section className="py-16">
      <div className="container max-w-3xl">
        <h2 className="font-heading text-2xl font-bold mb-6">Doctor Profile</h2>
        <p className="text-muted-foreground mb-6">
          {DOCTOR_NAME} is a highly skilled dental surgeon specializing in implantology, prosthodontics,
          and cosmetic dentistry. With a commitment to painless, patient-centric care, she has helped
          hundreds of patients in Kalyan and surrounding areas achieve their dream smiles.
        </p>
        <h3 className="font-heading text-lg font-semibold mb-4">Qualifications & Certifications</h3>
        <div className="space-y-3 mb-8">
          {qualifications.map((q) => (
            <div key={q.degree} className="flex items-start gap-3 p-3 bg-accent/50 rounded-lg">
              <GraduationCap className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sm">{q.degree}</p>
                <p className="text-xs text-muted-foreground">{q.institution}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 bg-accent/50">
      <div className="container">
        <h2 className="font-heading text-2xl font-bold mb-6 text-center">Our Team</h2>
        <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-8">
          A warm, skilled team of dentists, hygienists and support staff — led by {DOCTOR_NAME} and dedicated to your comfort.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {[teamGroup1, teamGroup2].map((img, i) => (
            <motion.div
              key={i}
              className="aspect-[4/3] rounded-xl overflow-hidden bg-muted shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
            >
              <img src={img} alt={`Mutha's Advanced Dental Care team photo ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" width={800} height={600} loading="lazy" decoding="async" />
            </motion.div>
          ))}
        </div>

        <h2 className="font-heading text-2xl font-bold mb-6 text-center">Our Clinic</h2>
        <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-8">
          Mutha's Advanced Dental Care was founded with a vision to provide world-class dental care in an accessible,
          comfortable setting. Our clinic is equipped with the latest dental technology.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[clinic1, clinic2, clinic3, clinic4].map((img, i) => (
            <motion.div
              key={i}
              className="aspect-square rounded-xl overflow-hidden bg-muted"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
            >
              <img src={img} alt={`Mutha's Advanced Dental Care clinic interior ${i + 1}`} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" width={300} height={300} loading="lazy" decoding="async" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16">
      <div className="container">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
            >
              <Card className="text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <CardContent className="p-6">
                  <stat.icon className="h-8 w-8 text-primary mx-auto mb-3" />
                  <p className="font-heading text-3xl font-bold mb-1">{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <CTABanner />
  </div>
);

export default About;
